import type { TenantRole } from '@endcopilot/shared';

export const ROLE_LABEL: Record<TenantRole | 'SUPERADMIN', string> = {
  SUPERADMIN: 'Superadmin Smile',
  TENANT_ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  VIEWER: 'Leitura',
};

export const ROLE_DESCRIPTION: Record<TenantRole, string> = {
  TENANT_ADMIN: 'Gerencia usuários, grupos, dispositivos e políticas da empresa. Exige MFA.',
  OPERATOR: 'Opera dispositivos e envia comandos. Não gerencia usuários nem políticas.',
  VIEWER: 'Somente leitura.',
};

/** Rótulos legíveis para a trilha de auditoria. Ações desconhecidas aparecem com o código. */
export const AUDIT_LABEL: Record<string, string> = {
  'auth.login_succeeded': 'Login realizado',
  'auth.login_failed': 'Falha de login',
  'auth.password_ok_mfa_pending': 'Senha correta, aguardando MFA',
  'auth.mfa_enroll_started': 'Cadastro de MFA iniciado',
  'auth.mfa_enrolled': 'MFA ativado',
  'auth.mfa_failed': 'Código MFA inválido',
  'auth.mfa_recovery_used': 'Código de recuperação usado',
  'auth.mfa_recovery_failed': 'Código de recuperação inválido',
  'auth.mfa_recovery_regenerated': 'Códigos de recuperação regenerados',
  'auth.mfa_reset_by_superadmin': 'MFA resetado por superadmin',
  'auth.refresh_reuse_detected': 'Reuso de sessão detectado (sessões revogadas)',
  'auth.logout': 'Logout',
  'auth.password_changed': 'Senha alterada',
  'tenant.created': 'Empresa criada',
  'user.created': 'Usuário criado',
  'user.linked': 'Usuário existente vinculado',
  'group.created': 'Grupo criado',
  'superadmin.granted': 'Superadmin concedido',
  'enrollment_code.created': 'Código de pareamento gerado',
  'enrollment_code.revoked': 'Código de pareamento revogado',
  'device.enrolled': 'Dispositivo pareado',
  'device.profile_changed': 'Perfil efetivo do dispositivo mudou',
  'device.revoked': 'Dispositivo desvinculado pelo painel',
  'device.unenrolled_by_user': 'Dispositivo desvinculado pelo usuário',
  'command.queued': 'Comando enfileirado',
  'command.succeeded': 'Comando executado',
  'command.failed': 'Comando falhou',
};

export const dateTime = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

export const PROFILE_LABEL: Record<'MONITORING' | 'ASSISTED' | 'CORPORATE', string> = {
  MONITORING: 'Monitoramento',
  ASSISTED: 'Controle Assistido',
  CORPORATE: 'Controle Corporativo',
};

export const CONTROL_LABEL: Record<string, string> = {
  INVENTORY: 'Inventário do aparelho',
  HEARTBEAT: 'Heartbeat (último contato)',
  LAUNCHER_HOME: 'Launcher como tela inicial',
  VPN_DOMAIN_FILTER: 'Filtro de domínios (VPN)',
  UNINSTALL_BLOCK: 'Bloqueio de desinstalação',
  LOCK_TASK_KIOSK: 'Modo quiosque (Lock Task)',
  USER_RESTRICTIONS: 'Restrições do sistema',
  SILENT_APP_INSTALL: 'Instalação silenciosa de apps',
};

export const CONNECTIVITY_LABEL: Record<'online' | 'stale' | 'never' | 'revoked', string> = {
  online: 'Em contato',
  stale: 'Sem contato recente',
  never: 'Nunca contatou',
  revoked: 'Desvinculado',
};

export const COMMAND_LABEL: Record<string, string> = {
  PING: 'Testar conexão',
  REFRESH_INVENTORY: 'Atualizar inventário',
};

export const COMMAND_STATUS_LABEL: Record<string, string> = {
  QUEUED: 'Na fila',
  DELIVERED: 'Entregue',
  SUCCEEDED: 'Executado',
  FAILED: 'Falhou',
  EXPIRED: 'Expirado',
  CANCELED: 'Cancelado',
};

const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

export function bytes(n?: number): string {
  if (n == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}
